import React, { useEffect } from "react";
import PropTypes from "prop-types";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Box,
} from "@mui/material";
import {
    useCreateCategoryMutation,
    useUpdateCategoryMutation,
} from "../../../redux/service/modules/admin";
import { X } from "lucide-react";
import { FormField } from "../../../components/common/FormField";
import { ActionButtons } from "../../../components/common/ActionButton";
import { useForm, Controller } from "react-hook-form";
import { useNotification } from "../../../context/NotificationContext";
import CommonDialog from "../../../components/common/CommonDialog";

const CategoryDialog = ({ open, onClose, rowData, isEdit, title, ctaText }) => {
    const { control, handleSubmit, setValue, reset, setError } = useForm();
    const [createCategory, { isLoading: isCreating }] =
        useCreateCategoryMutation();
    const [updateCategory, { isLoading: isUpdating }] =
        useUpdateCategoryMutation();
    const { showNotification } = useNotification();

    useEffect(() => {
        if (rowData) {
            Object.keys(rowData).forEach((key) => {
                setValue(key, rowData[key]);
            });
        }
    }, [rowData, setValue]);

    const handleClose = () => {
        reset();
        onClose();
    };

    const onSubmit = async (data) => {
        try {
            const formDataToSend = new FormData();
            Object.keys(data).forEach((key) => {
                formDataToSend.append(key, data[key]);
            });
            if (isEdit) {
                await updateCategory({ data: formDataToSend })
                    .unwrap()
                    .then((response) => {
                        if (response) {
                            showNotification(
                                "success",
                                "Category Updated successfully!",
                                ""
                            );
                            handleClose();
                        } else {
                            showNotification(
                                "error",
                                "An Error Occurred",
                                "Failed to Update Category"
                            );
                        }
                    })
                    .catch((error) => {
                        if (error.status === 400) {
                            setError("category_name", {
                                type: "manual",
                                message: error.data.error,
                            });
                        }
                    });
            } else {
                await createCategory({ data: formDataToSend })
                    .unwrap()
                    .then((response) => {
                        if (response) {
                            showNotification(
                                "success",
                                "Category Created successfully!",
                                ""
                            );
                            handleClose();
                        } else {
                            showNotification(
                                "error",
                                "An Error Occurred",
                                "Failed to Create Category"
                            );
                        }
                    })
                    .catch((error) => {
                        if (error.status === 400) {
                            setError("category_name", {
                                type: "manual",
                                message: error.data.error,
                            });
                        }
                    });
            }
        } catch (error) {
            console.error("Failed to update category:", error);
        }
    };

    return (
        <CommonDialog open={open} onClose={handleClose} title={title}>
            <Controller
                name="category_name"
                control={control}
                rules={{
                    required: "Category name is required",
                    pattern: {
                        value: /^[a-zA-Z0-9\s\-\&\(\)\;\\/]+$/,
                        message:
                            "Category name can only contain letters, numbers, spaces, hyphens, ampersands, semicolons, forward slashes and parentheses",
                    },
                }}
                render={({ field, fieldState }) => (
                    <FormField
                        label="Category Name"
                        {...field}
                        required
                        error={!!fieldState.error} // Show error if exists
                        helperText={
                            fieldState.error ? fieldState.error.message : ""
                        } // Display error message
                    />
                )}
            />

            <ActionButtons
                onClose={handleClose}
                ctaText={ctaText}
                onSave={handleSubmit(onSubmit)}
                isLoading={isCreating || isUpdating}
            />
        </CommonDialog>
    );
};

CategoryDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    rowData: PropTypes.object,
};

export default CategoryDialog;
